import { addDays, differenceInCalendarDays, format, startOfDay } from "date-fns";

import { EditScheduledMessageRequest } from "@/pages/Private/redux/schedule/schedule.api";

import { DraftTiming, timingFromDays } from "./inboxDraft.types";

/** What the edit modal holds while the user types. */
export interface EditScheduledMessageForm {
	message: string;
	subject: string;
	/** The picker's selection: a preset or a custom date, as `TimingDropdown` emits it. */
	timing: DraftTiming;
}

/** The message as it was when the modal opened — what "changed" is measured against. */
export interface EditScheduledMessageOriginal {
	message: string;
	subject: string;
	/** ISO send time of a timed message; null for a follow-up parked behind an invite. */
	scheduledFor: string | null;
	/** Delay after acceptance of a parked follow-up; null for a timed message. */
	scheduledInDays: number | null;
}

/** A send time moved into the past by the picker lands this far ahead instead. */
const SEND_NOW_GRACE_MS = 5 * 60 * 1000;

/**
 * The picker's initial selection for a timed message: its current send time as a custom entry,
 * so the trigger shows the date the message is actually going out on (PREC-4220).
 */
export function timingFromScheduledFor(scheduledFor: string, now: Date): DraftTiming {
	const date = new Date(scheduledFor);

	return {
		id: "custom",
		label: format(date, "dd. MMMM"),
		shortLabel: format(date, "dd.MM"),
		value: date,
		scheduledInDays: differenceInCalendarDays(date, now),
	};
}

/** The picker's initial selection for a parked follow-up: its delay after acceptance. */
export function timingFromParkedDelay(scheduledInDays: number | null): DraftTiming {
	return timingFromDays(scheduledInDays ?? 1);
}

/**
 * The new absolute send time a picker selection means for a timed message. A preset or a custom
 * date names a day; the message keeps the time of day it was composed for, so "Tomorrow" on a
 * 09:00 message is tomorrow 09:00. "Now" — or a day that lands in the past — sends a few minutes
 * from now, which the API reads as "on the next pass".
 */
export function scheduledForFromTiming(
	timing: DraftTiming,
	originalScheduledFor: string,
	now: Date
): Date {
	if (timing.id === "now") {
		return new Date(now.getTime() + SEND_NOW_GRACE_MS);
	}

	const original = new Date(originalScheduledFor);
	const day = addDays(startOfDay(now), timing.scheduledInDays ?? 0);

	day.setHours(original.getHours(), original.getMinutes(), original.getSeconds(), 0);

	return day.getTime() < now.getTime() ? new Date(now.getTime() + SEND_NOW_GRACE_MS) : day;
}

/**
 * Only what the user changed travels (PREC-4220): the API refuses a field that does not fit the
 * row, so an untouched subject on a mail must not be re-sent as a subject on LinkedIn, and an
 * untouched timing must not become "a date on a parked follow-up".
 */
export function buildEditScheduledMessagePatch(
	scheduleId: number,
	original: EditScheduledMessageOriginal,
	form: EditScheduledMessageForm,
	now: Date
): EditScheduledMessageRequest {
	const patch: EditScheduledMessageRequest = { scheduleId };

	if (form.message !== original.message) {
		patch.message = form.message;
	}

	if (form.subject !== original.subject) {
		patch.subject = form.subject;
	}

	if (original.scheduledFor !== null) {
		const scheduledFor = scheduledForFromTiming(form.timing, original.scheduledFor, now);

		if (scheduledFor.toISOString() !== new Date(original.scheduledFor).toISOString()) {
			patch.scheduledFor = scheduledFor.toISOString();
		}
	} else if (
		form.timing.scheduledInDays !== undefined &&
		form.timing.scheduledInDays !== (original.scheduledInDays ?? 1)
	) {
		patch.scheduledInDays = form.timing.scheduledInDays;
	}

	return patch;
}

/** True when the patch carries nothing beyond the id — the Save button has nothing to do. */
export function isEmptyEditPatch(patch: EditScheduledMessageRequest): boolean {
	return Object.keys(patch).length === 1;
}
